import type { SampleShot } from '../lib/types';
import { useContent } from '../hooks/useSession';

/**
 * The six shots every listing needs, in the order a buyer wants to see them.
 *
 * Kept as a fallback for a database seeded before the slots existed — normally these
 * come from the content bundle, where staff can attach a reference photo to each one.
 */
const SHOTS: SampleShot[] = [
  { id: 'hero', label: 'Hero', hint: 'Whole product, straight on, plain background, fills the frame.', imageUrl: null },
  { id: 'back', label: 'Back', hint: 'The reverse, same light and background.', imageUrl: null },
  { id: 'detail', label: 'Detail', hint: 'Close on the weave, zari, joint or engraving.', imageUrl: null },
  { id: 'scale', label: 'Scale', hint: 'Next to a hand, a coin or a ruler.', imageUrl: null },
  { id: 'worn', label: 'In use', hint: 'Worn for apparel, in a room for homeware.', imageUrl: null },
  { id: 'included', label: 'What is included', hint: 'Everything laid flat together.', imageUrl: null },
];

/** The reference photo for a slot, sitting in the same square the placeholder fills. */
function SampleImage({ shot }: { shot: SampleShot }) {
  return (
    <div
      style={{
        position: 'relative',
        aspectRatio: '1 / 1',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
        background: 'var(--surface-sunken)',
        overflow: 'hidden',
      }}
    >
      <img
        src={shot.imageUrl ?? ''}
        alt={`Example of a ${shot.label.toLowerCase()} shot`}
        loading="lazy"
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
      <span
        style={{
          position: 'absolute', top: 8, left: 8,
          padding: '3px 8px', borderRadius: 999,
          background: 'rgba(11,31,59,0.78)', color: '#fff',
          fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-bold)', letterSpacing: '.08em',
        }}
      >
        EXAMPLE
      </span>
    </div>
  );
}

function ImagePlaceholder({ label }: { label: string }) {
  return (
    <div
      style={{
        aspectRatio: '1 / 1',
        borderRadius: 'var(--radius-md)',
        border: '1.5px dashed var(--border-strong)',
        background: 'var(--gray-100, #F1F2F4)',
        display: 'grid',
        placeContent: 'center',
        justifyItems: 'center',
        gap: 8,
        color: 'var(--text-subtle)',
      }}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
      <span style={{ fontSize: 'var(--fs-xs)' }}>{label}</span>
    </div>
  );
}

export function PhotosPage() {
  const content = useContent();
  const shots = content.sampleShots?.length ? content.sampleShots : SHOTS;
  const hasExamples = shots.some((s) => s.imageUrl);

  return (
    <div className="vu-wrap">
      <div className="vu-kicker">Tool</div>
      <h1 className="vu-h1">Photo standard, side by side</h1>
      <p className="vu-lede" style={{ maxWidth: '64ch' }}>
        Eight rules, each with the version that passes review and the version that gets
        rejected, then the six shots every listing needs — shoot yours to match.
      </p>

      <div
        className="vu-card"
        style={{ margin: 'var(--space-6) 0 var(--space-10)', padding: 0, overflow: 'hidden' }}
      >
        <table className="vu-table">
          <thead>
            <tr style={{ background: 'var(--surface-sunken)' }}>
              <th style={{ width: 170, paddingLeft: 'var(--space-5)' }}>Rule</th>
              <th style={{ color: 'var(--green-500)' }}>Passes</th>
              <th style={{ color: 'var(--red-500)' }}>Rejected</th>
            </tr>
          </thead>
          <tbody>
            {content.photoRules.map((r) => (
              <tr key={r.rule}>
                <td style={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-strong)', paddingLeft: 'var(--space-5)' }}>
                  {r.rule}
                </td>
                <td>{r.good}</td>
                <td className="vu-muted">{r.bad}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="vu-h2">The six shots, and what each one does</h2>
      <p className="vu-muted" style={{ margin: 'var(--space-2) 0 var(--space-5)', maxWidth: '70ch' }}>
        {hasExamples
          ? 'These are our own reference shots. Look at them together, not one at a time — consistency is the thing buyers read as professional.'
          : 'Look at them together, not one at a time — consistency is the thing buyers read as professional.'}
      </p>

      <div className="vu-grid vu-grid-3">
        {shots.map((shot) => (
          <div key={shot.id}>
            {shot.imageUrl ? <SampleImage shot={shot} /> : <ImagePlaceholder label={shot.label} />}
            <div style={{ fontWeight: 'var(--fw-bold)', color: 'var(--text-strong)', marginTop: 'var(--space-3)' }}>
              {shot.label}
            </div>
            <div className="vu-subtle" style={{ marginTop: 4, fontSize: 'var(--fs-xs)', lineHeight: 1.55 }}>
              {shot.hint}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
